import type { FC, MouseEventHandler } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';

import { Button } from '@sravni/react-design-system/lib/Button';
import { Icon } from '@sravni/react-design-system/lib/Icon';
import { Space } from '@sravni/react-design-system/lib/Space';
import { ArrowRight } from '@sravni/react-icons';
import { useBoolean, useIsMobile, useStyles } from '@sravni/react-utils';

import { useFilters } from '../../../../../store/hooks/filters';
import { useServices } from '../../../../../store/hooks/services';
import type { TService } from '../../../../../types/services';

import styles from './service-list.module.scss';

type ServiceListProps = {
  onChangeService: (service: TService) => MouseEventHandler;
  onClickMoreButton: MouseEventHandler;
};

export const ServiceList: FC<ServiceListProps> = ({ onChangeService, onClickMoreButton }) => {
  const isMobile = useIsMobile();
  const serviceButtonsRef = useRef<HTMLDivElement>(null);
  const cx = useStyles(styles);
  const [isMoreButtonVisible, setMoreButtonVisibility] = useBoolean(true);
  const { service: currentService, customerType } = useFilters();
  const allServices = useServices();
  const servicesForActualCustomerType = useMemo(() => allServices?.[customerType], [allServices, customerType]);
  const availableCustomerTypeCount = useMemo(() => Object.keys(allServices).length, [allServices]);

  const servicesToShow = useMemo(
    () => servicesForActualCustomerType.filter((service) => currentService?.key !== service.key),
    [currentService?.key, servicesForActualCustomerType],
  );

  useEffect(() => {
    const serviceButtonsContainer = serviceButtonsRef.current;

    const listener: ResizeObserverCallback = (entries) => {
      for (const entry of entries) {
        const container = entry.target;
        let isOverflowing = false;
        const buttons = Array.from(container.querySelectorAll('button:not(:last-child)'));

        buttons.forEach((button) => {
          button.classList.remove(cx('hidden'));
        });

        for (let i = buttons.length - 1; i >= 0; i--) {
          if (
            (!isMobile && container.scrollWidth > container.clientWidth) ||
            (isMobile && container.scrollHeight > container.clientHeight)
          ) {
            buttons[i].classList.add(cx('hidden'));
            isOverflowing = true;
            setMoreButtonVisibility.on();
          } else {
            break;
          }
        }

        if (!isOverflowing && !(availableCustomerTypeCount > 1)) {
          setMoreButtonVisibility.off();
        }
      }
    };

    const resizeObserver = new ResizeObserver(listener);
    serviceButtonsContainer && resizeObserver.observe(serviceButtonsContainer);

    return () => {
      serviceButtonsContainer && resizeObserver.unobserve(serviceButtonsContainer);
    };
  }, [availableCustomerTypeCount, cx, isMobile, setMoreButtonVisibility]);

  return (
    <Space
      size={8}
      direction="horizontal"
      className={cx('services', isMobile ? 'h-mt-16' : 'h-mt-12')}
      wrap={isMobile}
      ref={serviceButtonsRef}
    >
      {currentService && (
        <Button variant="primary" color="dark" key={currentService.key} size={36}>
          {currentService.label}
        </Button>
      )}
      {servicesToShow.map((service) => (
        <Button variant="outlined" color="dark" key={service.key} onClick={onChangeService(service)} size={36}>
          {service.label}
        </Button>
      ))}
      {isMoreButtonVisible && (
        <Button variant="outlined" color="dark" onClick={onClickMoreButton} size={36}>
          Ещё <Icon size={20} icon={<ArrowRight />} />
        </Button>
      )}
    </Space>
  );
};
